import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import {
  clienteProdutosApi,
  ListClienteProdutosParams,
} from '../../services/api/cliente-produtos.api';
import {
  ClienteProdutoDetail,
  ClienteProdutoListItem,
  ClienteProdutosFilters,
  PaginatedClienteProdutos,
} from '../../types/cliente-produto';

export interface ClienteProdutosState {
  items: ClienteProdutoListItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  filters: ClienteProdutosFilters;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  currentClienteId: string | null;
  selectedDetail: ClienteProdutoDetail | null;
  detailStatus: 'idle' | 'loading' | 'error';
  detailError: string | null;
}

const initialState: ClienteProdutosState = {
  items: [],
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  filters: {},
  status: 'idle',
  error: null,
  currentClienteId: null,
  selectedDetail: null,
  detailStatus: 'idle',
  detailError: null,
};

export const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.response?.status === 403)
      return 'Você não tem permissão para acessar este recurso.';
    if (error.code === 'ERR_NETWORK')
      return 'Não foi possível conectar ao servidor.';
  }
  return fallback;
};

export const fetchClienteProdutosThunk = createAsyncThunk<
  PaginatedClienteProdutos,
  { clienteId: string; params: ListClienteProdutosParams },
  { rejectValue: string }
>('clienteProdutos/fetch', async ({ clienteId, params }, { rejectWithValue }) => {
  try {
    return await clienteProdutosApi.listByCliente(clienteId, params);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar produtos'));
  }
});

export const fetchProdutoDetailThunk = createAsyncThunk<
  ClienteProdutoDetail,
  string,
  { rejectValue: string }
>('clienteProdutos/fetchDetail', async (id, { rejectWithValue }) => {
  try {
    return await clienteProdutosApi.detail(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar produto'));
  }
});

const clienteProdutosSlice = createSlice({
  name: 'clienteProdutos',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<ClienteProdutosFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearFilters(state) {
      state.filters = {};
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
      state.page = 1;
    },
    clearSelectedDetail(state) {
      state.selectedDetail = null;
      state.detailStatus = 'idle';
      state.detailError = null;
    },
    setCurrentClienteId(state, action: PayloadAction<string | null>) {
      if (state.currentClienteId !== action.payload) {
        state.items = [];
        state.page = 1;
        state.total = 0;
        state.totalPages = 0;
        state.filters = {};
        state.status = 'idle';
        state.error = null;
        state.selectedDetail = null;
        state.detailStatus = 'idle';
        state.detailError = null;
      }
      state.currentClienteId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClienteProdutosThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClienteProdutosThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload.data;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchClienteProdutosThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar produtos';
      })

      .addCase(fetchProdutoDetailThunk.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchProdutoDetailThunk.fulfilled, (state, action) => {
        state.detailStatus = 'idle';
        state.selectedDetail = action.payload;
      })
      .addCase(fetchProdutoDetailThunk.rejected, (state, action) => {
        state.detailStatus = 'error';
        state.detailError = action.payload ?? 'Erro ao carregar produto';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setLimit,
  clearSelectedDetail,
  setCurrentClienteId,
} = clienteProdutosSlice.actions;

export default clienteProdutosSlice.reducer;
